import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { useLoaderData } from 'react-router';

import { EyeIcon, BookmarkIcon, BookOpenIcon } from 'lucide-react';
import { shortenCount, formatTimeSpent } from '@rocco/utils/format-number';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { NumberedAccordion, CONTENT_CAP } from '~/ui-rocco/pages/detail-shared';
import { useBookMeetingAccordion } from '../hooks/use-book-meeting-accordion';
import { useProjectAccordionRows } from '../hooks/use-project-accordion-rows';

/**
 * P5 — the numbered accordion (Project Details / Project Team / Book a Meeting /
 * Get in touch) plus the engagement-stats strip, on the dark content background.
 * The rows are built in {@link useProjectAccordionRows} so the About-the-Supplier
 * section can number itself as the next item after them. Both states of the (03)
 * row now have inline content (booking calendar or the "Get in touch" map +
 * enquiry form), so the row simply toggles — no modal fallback.
 */
export const ProjectAccordionSection = () => {
    const { openRows, setOpenRows } = useBookMeetingAccordion();
    const { rows } = useProjectAccordionRows();

    return (
        <>
            <NumberedAccordion
                headingLevel={2}
                theme="dark"
                multiple
                rows={rows}
                value={openRows}
                onValueChange={(value) => setOpenRows(value as string[])}
            />
            <ProjectStats />
        </>
    );
};

/** Engagement-stats strip (Views · Saves · Time spent) from the project KPI. */
const ProjectStats = () => {
    const { project } = useLoaderData<typeof loader>();
    const kpi = project.PublicKPI;
    if (!kpi) return null;

    const totalViews = (kpi.ItemPageViews ?? 0) + (kpi.ItemImpressions ?? 0);
    const stats: { icon: ReactNode; value: string }[] = [];
    if (totalViews > 100) {
        stats.push({
            icon: <EyeIcon className="size-5 text-white" />,
            value: shortenCount(totalViews),
        });
    }
    if ((kpi.ItemSaves ?? 0) > 100) {
        stats.push({
            icon: <BookmarkIcon className="size-5 text-white" />,
            value: shortenCount(kpi.ItemSaves ?? 0),
        });
    }
    if ((kpi.ItemTimeSpent ?? 0) > 0) {
        stats.push({
            icon: <BookOpenIcon className="size-5 text-white" />,
            value: formatTimeSpent(kpi.ItemTimeSpent ?? 0),
        });
    }
    if (stats.length === 0) return null;

    return (
        <div
            className={cn(
                CONTENT_CAP,
                'flex items-center justify-end gap-8 pt-5 text-white max-md:gap-2.5 max-md:px-5 md:px-[3rem] md:pt-[1.44rem]',
            )}
        >
            {stats.map((stat, index) => (
                <span
                    key={index}
                    // min-width (not a fixed width) so the pills keep a uniform
                    // baseline but a longer value like "890h 22m" expands on one
                    // line instead of wrapping inside a fixed w-32. On mobile the
                    // three pills share the row, so drop the min-width and tighten
                    // the padding so they fit without overflowing the gutter.
                    className="flex h-10 min-w-32 items-center justify-center gap-2 rounded-[1.875rem] border border-[#767676] py-3 font-sans whitespace-nowrap max-md:min-w-0 max-md:px-3.5 max-md:text-sm md:px-6 md:text-[1.128rem]"
                >
                    {stat.icon}
                    {stat.value}
                </span>
            ))}
        </div>
    );
};
